import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSmartPolling } from '@/hooks/useSmartPolling';

export interface ChipFila {
  /** Quantidade total */
  total: number;
  /** Horas do item mais antigo aguardando (0 se vazio) */
  maisAntigoHoras: number;
}

export interface FilaTrabalhoBKO {
  triagemInspecao: ChipFila;
  smsAnatel: ChipFila;
  bv: ChipFila & {
    /** id da conversa do cliente mais antigo respondido (deep-link) */
    proximaConversaId: string | null;
  };
  fatura: ChipFila & {
    proximaConversaId: string | null;
  };
  conversasEsperando: ChipFila & {
    proximaConversaId: string | null;
  };
}

function horasDesde(iso: string | null | undefined): number {
  if (!iso) return 0;
  return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60);
}

/**
 * Agrega os 5 contadores do banner de trabalho do BKO Portabilidade.
 * Só roda quando profile.funcao === 'BKO_PORTABILIDADE'.
 * RLS já isola pedidos/conversas por equipe/owner.
 */
export function useBKOPortabilidadeFilaTrabalho() {
  const { profile } = useAuth();
  const { refetchInterval } = useSmartPolling({ weight: 'B' });
  const enabled = !!profile?.id && profile?.funcao === 'BKO_PORTABILIDADE';

  return useQuery<FilaTrabalhoBKO>({
    queryKey: ['bko-port-fila-trabalho', profile?.id],
    enabled,
    refetchInterval,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async () => {
      const empty: FilaTrabalhoBKO = {
        triagemInspecao: { total: 0, maisAntigoHoras: 0 },
        smsAnatel: { total: 0, maisAntigoHoras: 0 },
        bv: { total: 0, maisAntigoHoras: 0, proximaConversaId: null },
        fatura: { total: 0, maisAntigoHoras: 0, proximaConversaId: null },
        conversasEsperando: { total: 0, maisAntigoHoras: 0, proximaConversaId: null },
      };
      if (!profile?.id) return empty;

      // Datas de corte: BV/Fatura considera respondidos nos últimos 7 dias sem fechamento
      const seteDiasAtras = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [
        triagemRes,
        smsRes,
        bvRes,
        faturaRes,
        esperandoRes,
      ] = await Promise.all([
        // 1. Triagem de inspeção (INSPEÇÃO CONCLUIDA aguardando atuação)
        supabase
          .from('v_planilha_mae')
          .select('item_id, entrada_coluna_em')
          .eq('quadro_atual', 'PRE_ATIVO')
          .eq('coluna_atual', 'INSPEÇÃO CONCLUIDA')
          .order('entrada_coluna_em', { ascending: true })
          .limit(500),

        // 2. SMS Anatel (SMS ENVIADO aguardando ação)
        supabase
          .from('v_planilha_mae')
          .select('item_id, entrada_coluna_em')
          .eq('quadro_atual', 'PRE_ATIVO')
          .eq('coluna_atual', 'SMS ENVIADO')
          .order('entrada_coluna_em', { ascending: true })
          .limit(500),

        // 3. BV — clientes que responderam a nota de boas-vindas e ainda estão pendentes
        supabase
          .from('qualidade_ciclo')
          .select('id, cliente_id, nota_recebida_em')
          .not('nota_recebida_em', 'is', null)
          .is('concluido_em', null)
          .gte('nota_recebida_em', seteDiasAtras)
          .order('nota_recebida_em', { ascending: true })
          .limit(200),

        // 4. Fatura — clientes que responderam a fatura e estão pendentes
        supabase
          .from('qualidade_ciclo')
          .select('id, cliente_id, fatura_resposta_em')
          .not('fatura_resposta_em', 'is', null)
          .is('concluido_em', null)
          .gte('fatura_resposta_em', seteDiasAtras)
          .order('fatura_resposta_em', { ascending: true })
          .limit(200),

        // 5. Conversas esperando (cliente chamou sem ser por disparo)
        // = inbound espontâneo: campanha_id NULL + unread > 0 + status ativo
        supabase
          .from('whatsapp_conversations')
          .select('id, last_inbound_at')
          .eq('owner_user_id', profile.id)
          .is('campanha_id', null)
          .gt('unread_count', 0)
          .not('status', 'in', '("finalizada","arquivada")')
          .order('last_inbound_at', { ascending: true })
          .limit(200),
      ]);

      // Helpers para mapear cliente_id -> conversa
      const bvCli = (bvRes.data || []).map((r: any) => r.cliente_id).filter(Boolean);
      const fatCli = (faturaRes.data || []).map((r: any) => r.cliente_id).filter(Boolean);
      const todosCli = Array.from(new Set([...bvCli, ...fatCli]));

      // Mapa cliente_id -> telefone -> conversa
      let conversaPorCliente = new Map<string, string>();
      if (todosCli.length > 0) {
        const { data: clientesData } = await supabase
          .from('clientes')
          .select('id, whatsapp, telefone_principal')
          .in('id', todosCli);
        const fones = new Map<string, string>(); // fone (11 últimos dígitos) -> cliente_id
        for (const c of clientesData || []) {
          const raw = (c as any).whatsapp || (c as any).telefone_principal || '';
          const dig = String(raw).replace(/\D/g, '');
          if (dig.length >= 10) {
            fones.set(dig.slice(-11), (c as any).id);
          }
        }
        if (fones.size > 0) {
          // wa_id costuma vir como "55<DDD><numero>" (12-13 dígitos)
          // fazemos match pelo sufixo de 11 dígitos (DDD+celular) — convenção do projeto
          const { data: convData } = await supabase
            .from('whatsapp_conversations')
            .select('id, wa_id')
            .eq('owner_user_id', profile.id);
          for (const conv of convData || []) {
            const wa = String((conv as any).wa_id || '').replace(/\D/g, '');
            if (wa.length < 10) continue;
            const cid = fones.get(wa.slice(-11));
            if (cid && !conversaPorCliente.has(cid)) {
              conversaPorCliente.set(cid, (conv as any).id);
            }
          }
        }
      }

      // BV
      const bvList = bvRes.data || [];
      const bvProxCli = bvList.find((r: any) => conversaPorCliente.has(r.cliente_id))?.cliente_id;
      const bv: FilaTrabalhoBKO['bv'] = {
        total: bvList.length,
        maisAntigoHoras: horasDesde((bvList[0] as any)?.nota_recebida_em),
        proximaConversaId: bvProxCli ? conversaPorCliente.get(bvProxCli) ?? null : null,
      };

      // Fatura
      const fatList = faturaRes.data || [];
      const fatProxCli = fatList.find((r: any) => conversaPorCliente.has(r.cliente_id))?.cliente_id;
      const fatura: FilaTrabalhoBKO['fatura'] = {
        total: fatList.length,
        maisAntigoHoras: horasDesde((fatList[0] as any)?.fatura_resposta_em),
        proximaConversaId: fatProxCli ? conversaPorCliente.get(fatProxCli) ?? null : null,
      };

      // Conversas esperando
      const espList = esperandoRes.data || [];
      const conversasEsperando: FilaTrabalhoBKO['conversasEsperando'] = {
        total: espList.length,
        maisAntigoHoras: horasDesde((espList[0] as any)?.last_inbound_at),
        proximaConversaId: (espList[0] as any)?.id ?? null,
      };

      return {
        triagemInspecao: {
          total: (triagemRes.data || []).length,
          maisAntigoHoras: horasDesde((triagemRes.data?.[0] as any)?.entrada_coluna_em),
        },
        smsAnatel: {
          total: (smsRes.data || []).length,
          maisAntigoHoras: horasDesde((smsRes.data?.[0] as any)?.entrada_coluna_em),
        },
        bv,
        fatura,
        conversasEsperando,
      };
    },
  });
}